import { isbot } from "isbot";
import { UAParser } from "ua-parser-js";
import { WebAttribution } from "./types";
import { getUtmAndCohort } from "./utils";
/**
 * Initializes and tracks web attribution events for the given user and session.
 *
 * This function:
 * - Loads and configures the Ampli instance (via `loadAmpli`).
 * - Collects and identifies user attributes, including device, browser, UTM parameters, and additional user data.
 * - Tracks a "web_attribution" event.
 * - Sends user data to a backend attribution service for further processing.
 *
 * @param {IWebAttributionParams} params - The parameters required for web attribution tracking.
 * @param {string} params.projectName - The name of the project for attribution tracking.
 * @param {TEnvironment} params.environment - The environment in which the application is running
 *   (e.g., 'production', 'development').
 * @param {IAmpli} params.ampli - The Ampli instance to load and use for tracking.
 * @param {string} params.amplitudeUserId - The user's unique identifier used in analytics and payments.
 * @param {Record<string, string> | undefined} [params.abTests] - A dictionary of A/B test variants for the user.
 * @param {Record<string, string> | undefined} [params.additionalUserData] - Additional user data that cannot be derived
 *   within web attribution (e.g., custom metadata or non-standard fields: product_id, country).
 * @param {amplitude.Types.BrowserOptions | undefined} [params.configuration] - Optional Amplitude client configuration.
 * @param {boolean | undefined} [params.disableSessionReplayTracking=false] - Whether to disable session replay tracking.
 *   Defaults to `false` (tracking enabled by default).
 * @param {number | undefined} [params.sessionReplaySampleRate] - The sampling rate for session replay tracking.
 *   Defaults to 0.1 in 'production' or 0 in other environments if not specified.
 *
 * @returns {Promise<boolean>} Resolves to a boolean indicating whether the Ampli instance was successfully loaded.
 *
 * @remarks
 * - Session replay tracking is **enabled by default** unless `disableSessionReplayTracking` is set to `true`.
 * - Sends a POST request to an attribution service with the collected user data.
 * - Collects additional metadata about the user's browser, device, and operating system using `ua-parser-js`.
 * - Includes `additionalUserData` to provide custom fields that are not derived within this function.
 */
export const _webAttribution = async (params) => {
    const { projectName, environment, ampli, configuration, disableSessionReplayTracking, sessionReplaySampleRate, abTests, additionalUserData, amplitudeUserId, } = params;
    try {
        _loadAmpli(ampli, environment, configuration, disableSessionReplayTracking, sessionReplaySampleRate);
        const deviceInfo = new UAParser().getResult();
        const { utm, cohort, initialUtm } = getUtmAndCohort();
        const sharedUserData = JSON.parse(JSON.stringify({
            project_name: projectName,
            device_category: deviceInfo.device.type || "desktop",
            browser: deviceInfo.browser.name || "unknown",
            browser_version: deviceInfo.browser.version || "unknown",
            operating_system: deviceInfo.os.name || "unknown",
            operating_system_version: deviceInfo.os.version || "unknown",
            device_model: deviceInfo.device.model || "unknown",
            device_brand: deviceInfo.device.vendor || "unknown",
            device_language: navigator.language || "unknown",
            is_test: deriveIsTest(),
            is_bot: deriveIsBot(),
            ...utm,
            ...cohort,
            ...initialUtm,
            ...additionalUserData,
        }));
        const res = await ampli.identify(amplitudeUserId, JSON.parse(JSON.stringify({
            ...sharedUserData,
            user_id: amplitudeUserId,
            ...abTests,
        }))).promise;
        if (!res) {
            throw new Error("amplitude event cannot be empty");
        }
        const device_id = res?.event?.device_id;
        ampli.track(new WebAttribution({
            ...sharedUserData,
            user_id: amplitudeUserId,
            device_id,
        }));
        const url = environment === "production"
            ? "https://attribution-production.apitlm-protected.com/web-user-info"
            : "https://attribution-development.apitlm-protected.com/web-user-info";
        fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...sharedUserData,
                user_id: amplitudeUserId,
                device_id,
            }),
        });
    }
    catch (e) {
        console.error(e);
    }
};
/**
 * Loads and initializes the Ampli instance with the specified configuration.
 *
 * This function ensures the Ampli instance is loaded only once during the page lifecycle.
 * By default, it also sets up session replay tracking unless explicitly disabled.
 *
 * @param {IAmpli} ampli - The Ampli instance to load and configure.
 * @param {string} stringEnvironment - The raw environment string that will be mapped to either 'development'
 *   or 'production'. Any value other than 'development' will be treated as 'production'
 * @param {amplitude.Types.BrowserOptions | undefined} configuration - Optional configuration
 *   for Amplitude's client setup.
 * @param {boolean} [disableSessionReplayTracking=false] - A flag to disable session replay
 *   tracking. Defaults to false (tracking enabled).
 * @param {number | undefined} sessionReplaySampleRate - The sampling rate for session replay
 *   tracking. If not provided, defaults to 0.1 in 'production' or 0 in other environments.
 *
 * @remarks
 * - The function checks if the Ampli instance is already loaded before proceeding.
 * - Session replay tracking is **enabled by default** unless `disableSessionReplayTracking` is set to `true`.
 * - If session replay tracking is enabled, it uses the specified or default sample rate.
 */
export const _loadAmpli = async (ampli, stringEnvironment, configuration, disableSessionReplayTracking = false, sessionReplaySampleRate) => {
    if (!ampli.isLoaded) {
        ampli.load({
            environment: stringEnvironment,
            client: {
                configuration: {
                    defaultTracking: {
                        attribution: false,
                    },
                    ...configuration,
                },
            },
        });
        // Since existing projects use only one environment, which is usually neither "development" nor "production",
        // we need to map it to either "development" or "production".
        const environment = stringEnvironment === "development" ? "development" : "production";
        if (!disableSessionReplayTracking) {
            const sampleRate = sessionReplaySampleRate ?? (environment === "production" ? 0.1 : 0);
            const { sessionReplayPlugin } = await import("@amplitude/plugin-session-replay-browser");
            const sessionReplayTracking = sessionReplayPlugin({
                sampleRate,
            });
            ampli.client.add(sessionReplayTracking);
        }
    }
};
/**
 * Determines if the current session should be marked as a test session.
 * Checks for 'is_test=true' in the URL and stores the flag in localStorage.
 * @returns {boolean} True if the session is a test session; otherwise, false.
 */
export const deriveIsTest = () => {
    const isTestFromStorage = localStorage.getItem("is_test");
    if (isTestFromStorage === null && location.href.includes("is_test=true")) {
        localStorage.setItem("is_test", "true");
        return true;
    }
    return isTestFromStorage === "true";
};
/**
 * Determines if the current user agent belongs to a bot.
 * Utilizes the `isbot` library to analyze the user agent string obtained from `navigator.userAgent`.
 * This function should be called only in a browser context, as it relies on the `navigator` object.
 * @throws {Error} Throws an error if called outside of a browser context (e.g., on the server side in SSR).
 * @returns {boolean} True if the user agent is recognized as a bot; otherwise, false.
 */
export const deriveIsBot = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        throw new Error(`${deriveIsBot.name} can only be called in a browser context`);
    }
    return isbot(navigator.userAgent);
};
